import { useEffect, useState } from "react";
import { Link } from "react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { DEFAULT_BRIEFING_FOCUS, WEEKDAY_NAMES, type GroupBriefing } from "@krut/shared";
import { api, unwrap } from "../api.ts";
import { Badge, Button, Card, cx, ErrorText, Input, PageHeader, Pagination, Select, Spinner, Switch } from "../components/ui.tsx";
import { keepPreviousData, usePaging } from "../hooks.ts";
import { groupPath } from "../lib/admin-nav.ts";

type Data = Awaited<ReturnType<typeof load>>;
const load = () => unwrap(api.admin.briefings.$get());

/**
 * Assistants → Briefings: each person's assistant reaches out on its own — a hello when their workspace is
 * created, and a short daily briefing. Admins switch it on, say what to focus on, and set each group's times.
 */
export function BriefingsPage() {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ["briefings"], queryFn: load });
  if (q.isPending) return <div className="grid place-items-center py-16"><Spinner /></div>;
  if (q.error) return <ErrorText error={q.error} />;
  const invalidate = () => void qc.invalidateQueries({ queryKey: ["briefings"] });
  return (
    <>
      <PageHeader
        title="Briefings"
        description="Each person's assistant reaches out on its own: a hello when their workspace is created, a short daily briefing — what to focus on today, what changed in their connected apps and in the company, a check-in on one of their goals, and one thing the assistant could take off their plate — and a weekly retrospective to review the week and their goals. Delivered as a direct message in their linked chat app, else by email with a link, and always kept as a chat in the app."
      />
      <SettingsCard key={JSON.stringify(q.data.settings)} data={q.data} onSaved={invalidate} />
      <GroupsCard data={q.data} onSaved={invalidate} />
      <TryItCard />
      <RunsCard />
    </>
  );
}

function SettingsCard({ data, onSaved }: { data: Data; onSaved: () => void }) {
  const { enabled, onboarding, focus, weekly } = data.settings;
  const [form, setForm] = useState({ enabled, onboarding, focus, weekly });
  const save = useMutation({ mutationFn: () => unwrap(api.admin.briefings.$put({ json: form })), onSuccess: onSaved });
  const hello = useMutation({ mutationFn: () => unwrap(api.admin.briefings["onboard-everyone"].$post()) });
  const dirty = JSON.stringify(form) !== JSON.stringify({ enabled, onboarding, focus, weekly });
  const chat = data.delivery.chatApps;
  return (
    <form
      className="space-y-6"
      onSubmit={(e) => {
        e.preventDefault();
        save.mutate();
      }}
    >
      <Card className="p-6">
        <div className="grid gap-x-8 sm:grid-cols-2">
          <Switch
            label="Send daily briefings"
            hint="At each group's times (below), in the person's own time zone. People can move or mute theirs under Schedules & briefings."
            checked={form.enabled}
            onChange={(v) => setForm({ ...form, enabled: v })}
          />
          <Switch
            label="Say hello to new workspaces"
            hint="When someone's assistant is created it introduces itself, asks what they do and what their longer-term goals are, and invites them to connect Gmail — explaining that what they connect stays private to them."
            checked={form.onboarding}
            onChange={(v) => setForm({ ...form, onboarding: v })}
          />
          <Switch
            label="Send a weekly retrospective"
            hint="Once a week (Friday 16:00 by default — set per group below) the assistant looks back at the week and each goal, then talks with the person about changing goals, moving deadlines or adding new ones."
            checked={form.weekly}
            onChange={(v) => setForm({ ...form, weekly: v })}
          />
        </div>
        <p className="mt-2 text-xs text-[var(--muted)]">Daily briefings also check in on one of the person's goals each day. People see and edit their goals on their Goals page.</p>
        <p className="mt-3 text-xs text-[var(--muted)]">
          Delivery: {chat.length ? `direct messages on ${chat.join(", ")} for people who linked their account` : "no chat app is set up yet (Workspace → Messaging bots)"}; {data.delivery.email ? "otherwise an email with a link" : "email is not configured, so everyone else sees briefings in the web app only"}.
          {" "}Each briefing is a turn of the person's own assistant and counts towards their budget.
        </p>
        {data.settings.onboarding && (
          <div className="mt-3 flex flex-wrap items-center gap-3 text-sm">
            <Button type="button" disabled={hello.isPending} onClick={() => confirm("Send the hello to everyone who never got one? Each assistant will message its person now.") && hello.mutate()}>
              {hello.isPending ? "Sending…" : "Say hello to everyone who hasn't had it"}
            </Button>
            {hello.data && <span className="text-[var(--muted)]">{hello.data.queued ? `Queued for ${hello.data.queued} ${hello.data.queued === 1 ? "person" : "people"}.` : "Everyone has had it."}</span>}
            <ErrorText error={hello.error} />
          </div>
        )}
      </Card>

      <Card className="p-6">
        <h2 className="mb-1 text-base font-semibold">What briefings focus on</h2>
        <p className="mb-4 text-sm text-[var(--muted)]">Plain language for every assistant: what the company wants people to pay attention to. The assistant adds what it knows about the person, their connected apps and shared company knowledge.</p>
        <textarea
          rows={8}
          value={form.focus}
          onChange={(e) => setForm({ ...form, focus: e.target.value })}
          className="w-full rounded-lg border border-[var(--line)] bg-[var(--panel)] px-3 py-2 font-mono text-xs leading-relaxed outline-none focus:border-[var(--color-accent)] focus:ring-2 focus:ring-[var(--color-accent)]/20"
        />
        <div className="mt-2 flex justify-end">
          <button type="button" className="text-sm text-[var(--color-accent)] hover:underline disabled:opacity-50" disabled={form.focus === DEFAULT_BRIEFING_FOCUS} onClick={() => setForm({ ...form, focus: DEFAULT_BRIEFING_FOCUS })}>
            Reset to default
          </button>
        </div>
      </Card>
      <ErrorText error={save.error} />
      <div className="flex items-center justify-end gap-3">
        {save.isSuccess && !dirty && <span className="text-sm text-[var(--muted)]">Saved</span>}
        <Button type="submit" variant="primary" disabled={save.isPending || !dirty}>Save</Button>
      </div>
    </form>
  );
}

const WEEKDAYS_LABEL = (b: GroupBriefing) => `${b.times.join(", ")}${b.weekdaysOnly ? " · weekdays" : " · every day"}`;
const WEEKLY_LABEL = (b: GroupBriefing) => `${WEEKDAY_NAMES[b.weeklyDay]} ${b.weeklyTime}`;

function GroupsCard({ data, onSaved }: { data: Data; onSaved: () => void }) {
  return (
    <Card className="mt-6 overflow-hidden">
      <div className="border-b border-[var(--line)] px-6 py-4">
        <h2 className="text-base font-semibold">Per group</h2>
        <p className="mt-1 text-sm text-[var(--muted)]">When each group's briefing goes out, in each person's own time zone. Up to three times a day. A person can pick their own times or mute it under Schedules & briefings. Each group's page has the same settings, under Budget & schedule.</p>
      </div>
      <ul className="divide-y divide-[var(--line)]">
        {data.groups.map((g) => (
          <GroupRow key={g.id + JSON.stringify(g.briefing)} group={g} weeklyOn={data.settings.weekly} onSaved={onSaved} />
        ))}
      </ul>
    </Card>
  );
}

function GroupRow({ group, weeklyOn, onSaved }: { group: Data["groups"][number]; weeklyOn: boolean; onSaved: () => void }) {
  const [open, setOpen] = useState(false);
  return (
    <li className="px-6 py-3 text-sm">
      <div className="flex flex-wrap items-center gap-3">
        <span className="min-w-0 flex-1">
          <Link to={groupPath(group.id, "budget")} className="font-medium hover:underline">
            {group.name}
          </Link>{" "}
          <span className="text-xs text-[var(--muted)]">· {group.members} {group.members === 1 ? "member" : "members"}</span>
        </span>
        {group.briefing.enabled ? <Badge tone="good">{WEEKDAYS_LABEL(group.briefing)}</Badge> : <Badge>Daily off</Badge>}
        {weeklyOn && (group.briefing.weeklyEnabled ? <Badge tone="accent">Weekly · {WEEKLY_LABEL(group.briefing)}</Badge> : <Badge>Weekly off</Badge>)}
        <Button type="button" variant="ghost" className="h-8" onClick={() => setOpen(!open)}>{open ? "Close" : "Change"}</Button>
      </div>
      {open && <GroupBriefingForm groupId={group.id} initial={group.briefing} weeklyOn={weeklyOn} onSaved={() => (setOpen(false), onSaved())} className="mt-3 rounded-lg bg-[var(--panel-2)] p-4" />}
    </li>
  );
}

/** A group's briefing times: the daily one (up to three a day) and the weekly retrospective. Also on the group's own page. */
export function GroupBriefingForm({ groupId, initial, weeklyOn, onSaved, className }: { groupId: string; initial: GroupBriefing; weeklyOn: boolean; onSaved: () => void; className?: string }) {
  const [b, setB] = useState<GroupBriefing>(initial);
  const [savedAs, setSavedAs] = useState(initial);
  const save = useMutation({
    mutationFn: () => unwrap(api.admin.briefings.groups[":id"].$put({ param: { id: groupId }, json: b })),
    // The server sorts the times and drops repeats: show what it kept.
    onSuccess: (r) => {
      setB(r.briefing);
      setSavedAs(r.briefing);
      onSaved();
    },
  });
  const dirty = JSON.stringify(b) !== JSON.stringify(savedAs);
  const setTime = (i: number, v: string) => setB({ ...b, times: b.times.map((t, j) => (j === i ? v : t)) });
  return (
    <div className={cx("grid gap-4 sm:grid-cols-2", className)}>
      <Switch label="Briefings for this group" checked={b.enabled} onChange={(v) => setB({ ...b, enabled: v })} />
      <Switch label="Weekdays only" hint="No briefing on Saturdays and Sundays." checked={b.weekdaysOnly} onChange={(v) => setB({ ...b, weekdaysOnly: v })} />
      <div className="sm:col-span-2">
        <div className="mb-1 text-sm font-medium">Times (local to each person)</div>
        <div className="flex flex-wrap items-center gap-2">
          {b.times.map((t, i) => (
            <span key={i} className="flex items-center gap-1">
              <Input type="time" value={t} onChange={(e) => setTime(i, e.target.value)} className="w-32" aria-label={`Briefing time ${i + 1}`} />
              {b.times.length > 1 && (
                <button type="button" className="text-xs text-[var(--muted)] hover:text-red-700" onClick={() => setB({ ...b, times: b.times.filter((_, j) => j !== i) })}>
                  remove
                </button>
              )}
            </span>
          ))}
          {b.times.length < 3 && (
            <button type="button" className="text-sm text-[var(--color-accent)] hover:underline" onClick={() => setB({ ...b, times: [...b.times, "16:00"] })}>
              + another time
            </button>
          )}
        </div>
      </div>
      <div className="border-t border-[var(--line)] pt-3 sm:col-span-2">
        <Switch label="Weekly retrospective for this group" hint={weeklyOn ? undefined : "The weekly retrospective is switched off for the whole company."} checked={b.weeklyEnabled} onChange={(v) => setB({ ...b, weeklyEnabled: v })} />
        {b.weeklyEnabled && (
          <div className="mt-2 flex flex-wrap items-center gap-2 text-sm">
            <span>Every</span>
            <span className="w-40">
              <Select value={b.weeklyDay} onChange={(e) => setB({ ...b, weeklyDay: Number(e.target.value) })} aria-label="Day of the week">
                {WEEKDAY_NAMES.map((d, i) => <option key={d} value={i}>{d}</option>)}
              </Select>
            </span>
            <span>at</span>
            <span className="w-32">
              <Input type="time" value={b.weeklyTime} onChange={(e) => setB({ ...b, weeklyTime: e.target.value })} aria-label="Weekly time" />
            </span>
            <span className="text-xs text-[var(--muted)]">local to each person</span>
          </div>
        )}
      </div>
      <div className="flex flex-wrap items-center gap-3 sm:col-span-2">
        <Button type="button" variant="primary" disabled={save.isPending || !dirty || b.times.some((t) => !/^\d\d:\d\d$/.test(t)) || !/^\d\d:\d\d$/.test(b.weeklyTime)} onClick={() => save.mutate()}>Save</Button>
        {save.isSuccess && !dirty && <span className="text-sm text-[var(--muted)]">Saved</span>}
        <ErrorText error={save.error} />
      </div>
    </div>
  );
}

function TryItCard() {
  const qc = useQueryClient();
  const [email, setEmail] = useState("");
  const [kind, setKind] = useState<"briefing" | "onboarding" | "weekly">("briefing");
  const send = useMutation({
    mutationFn: () => unwrap(api.admin.briefings.run.$post({ json: { email, kind } })),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["briefings", "runs"] }),
  });
  return (
    <Card className="mt-6 p-6">
      <h2 className="mb-1 text-base font-semibold">Send one now</h2>
      <p className="mb-3 text-sm text-[var(--muted)]">The quickest way to see what people get: their assistant writes it right away and it is delivered the same way as a scheduled one.</p>
      <form
        className="flex flex-wrap items-center gap-2"
        onSubmit={(e) => {
          e.preventDefault();
          send.mutate();
        }}
      >
        <Input type="email" required placeholder="person@company.com" value={email} onChange={(e) => setEmail(e.target.value)} className="max-w-xs" />
        <Select value={kind} onChange={(e) => setKind(e.target.value as typeof kind)} className="w-44">
          <option value="briefing">Daily briefing</option>
          <option value="onboarding">Hello (getting started)</option>
          <option value="weekly">Weekly retrospective</option>
        </Select>
        <Button type="submit" variant="primary" disabled={send.isPending || !email}>{send.isPending ? "Sending…" : "Send now"}</Button>
        {send.data && <span className="text-sm text-[var(--muted)]">Sent {send.data.deliveredVia === "web" ? "to the web app" : send.data.deliveredVia === "email" ? "by email" : `on ${send.data.deliveredVia}`}.</span>}
      </form>
      <ErrorText error={send.error} />
    </Card>
  );
}

const STATUS_TONE = { sent: "good", failed: "bad", skipped: "neutral", running: "accent" } as const;

function RunsCard() {
  const [kind, setKind] = useState<"all" | "briefing" | "onboarding" | "weekly">("all");
  const [status, setStatus] = useState<"all" | "sent" | "failed" | "skipped" | "running">("all");
  const paging = usePaging([kind, status]);
  const q = useQuery({
    queryKey: ["briefings", "runs", kind, status, paging.page, paging.pageSize, paging.q],
    queryFn: () => unwrap(api.admin.briefings.runs.$get({ query: { kind, status, page: String(paging.page), pageSize: String(paging.pageSize), ...(paging.q && { q: paging.q }) } })),
    placeholderData: keepPreviousData,
    refetchInterval: 15_000,
  });
  const total = q.data?.total;
  useEffect(() => void paging.clamp(total), [total]); // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <Card className="mt-6 overflow-hidden">
      <div className="flex flex-wrap items-center gap-2 border-b border-[var(--line)] px-4 py-3">
        <h2 className="mr-2 text-base font-semibold">What went out</h2>
        <Select value={kind} onChange={(e) => setKind(e.target.value as typeof kind)} className="h-9 w-40">
          <option value="all">All kinds</option>
          <option value="briefing">Briefings</option>
          <option value="onboarding">Hellos</option>
          <option value="weekly">Weekly retrospectives</option>
        </Select>
        <Select value={status} onChange={(e) => setStatus(e.target.value as typeof status)} className="h-9 w-36">
          <option value="all">Any status</option>
          <option value="sent">Sent</option>
          <option value="failed">Failed</option>
          <option value="skipped">Skipped</option>
          <option value="running">Running</option>
        </Select>
        <Input value={paging.search} onChange={(e) => paging.setSearch(e.target.value)} placeholder="Search by person or text…" className="max-w-xs" />
      </div>
      {q.isPending ? (
        <div className="grid place-items-center py-10"><Spinner /></div>
      ) : q.error ? (
        <div className="p-4"><ErrorText error={q.error} /></div>
      ) : !q.data.items.length ? (
        <p className="px-4 py-10 text-center text-sm text-[var(--muted)]">{paging.q ? "Nothing matches." : "Nothing sent yet."}</p>
      ) : (
        <ul className={cx("divide-y divide-[var(--line)]", q.isPlaceholderData && "opacity-60")}>
          {q.data.items.map((r) => (
            <li key={r.id} className="px-4 py-3 text-sm">
              <div className="flex flex-wrap items-center gap-2">
                <span className="font-medium">{r.name}</span>
                <span className="text-xs text-[var(--muted)]">{r.email}{r.groupName ? ` · ${r.groupName}` : ""}</span>
                <Badge tone={r.kind === "briefing" ? "neutral" : "accent"}>{r.kind === "onboarding" ? "Hello" : r.kind === "weekly" ? "Weekly" : "Briefing"}</Badge>
                <Badge tone={STATUS_TONE[r.status]}>{r.status}</Badge>
                {r.deliveredVia && <span className="text-xs text-[var(--muted)]">via {r.deliveredVia}</span>}
                <span className="ml-auto text-xs text-[var(--muted)]" title={r.slot}>{new Date(r.createdAt).toLocaleString()}</span>
              </div>
              {r.summary && <div className="mt-1 text-xs text-[var(--muted)]">{r.summary}</div>}
              {r.error && <div className="mt-1 text-xs text-red-700">{r.error}</div>}
            </li>
          ))}
        </ul>
      )}
      <Pagination page={paging.page} pageSize={paging.pageSize} total={total ?? 0} onPage={paging.setPage} onPageSize={paging.setPageSize} busy={q.isFetching} />
    </Card>
  );
}
